import { DataService } from "@/services/data";
import { PaginatedResponse } from "@/types/common";
import { MonthTarget, TargetComponentValue, WeekTarget } from "@/types/user/target";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, RouteObject } from "react-router-dom";

const TargetRoute: RouteObject = {
    path: "objectif",
    async lazy() {
        let { TargetLayout } = await import("@/pages/target/layout");
        return { Component: TargetLayout };
    },
    children: [
        {
            index: true,
            element: <Navigate to="mensuel" replace />
        },
        {
            path: "mensuel",
            async lazy() {
                let { TargetPage } = await import("@/pages/target/month-target");
                return { Component: TargetPage };
            },
            children: [
                {
                    path: "api",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const user = url.searchParams.get("user");
                        const month = url.searchParams.get("month");
                        const salesProcess = url.searchParams.get("sales_process");

                        if (!user || !month) {
                            return null;
                        }

                        const query: Record<string, string> = { user, month };
                        if (salesProcess) {
                            query.sales_process = salesProcess;
                        }

                        const month_target = (await DataService.client.url("/users/month_target/").query(query).get().json()) as PaginatedResponse<MonthTarget>;

                        if (month_target.results.length === 0) {
                            return null;
                        }

                        return month_target.results[0];
                    }
                },
                {
                    path: "team",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const month = url.searchParams.get("month");
                        const salesProcess = url.searchParams.get("sales_process");

                        if (!month) {
                            return [];
                        }

                        const results: MonthTarget[] = [];
                        let nextUrl: string | null = "/users/month_target/";
                        let isFirstPage = true;

                        while (nextUrl) {
                            const query: Record<string, string | number> = {
                                month,
                                page_size: 1000
                            };
                            if (salesProcess) {
                                query.sales_process = salesProcess;
                            }
                            const pageRequest = isFirstPage ? DataService.client.url(nextUrl).query(query) : DataService.client.url(nextUrl);
                            const page = (await pageRequest.get().json()) as PaginatedResponse<MonthTarget>;
                            results.push(...(page.results ?? []));
                            nextUrl = page.next ?? null;
                            isFirstPage = false;
                        }

                        return results;
                    }
                },
                {
                    path: ":month_target_id",
                    action: async ({ params, request }) => {
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        if (action) {
                            try {
                                const content = await DataService.client.url(`/users/month_target/${params.month_target_id}/${action}/`).post(data).json();
                                return handle_success(content);
                            } catch (error) {
                                return handle_error(error);
                            }
                        }
                    },
                    children: [
                        {
                            path: "components",
                            loader: async ({ params }) => {
                                const component_values = (await DataService.client
                                    .url("/users/target_component_value/")
                                    .query({
                                        target_component__month_target: params.month_target_id,
                                        page_size: 1000
                                    })
                                    .get()
                                    .json()) as PaginatedResponse<TargetComponentValue>;

                                return component_values.results;
                            }
                        },
                        {
                            path: "history",
                            loader: async ({ params }) => {
                                const history = await DataService.client.url(`/users/month_target/${params.month_target_id}/history/`).get().json();

                                return history;
                            }
                        },
                        {
                            path: "explain/:component_class/:at_date",
                            loader: async ({ params }) => {
                                try {
                                    const explain = await DataService.client.url(`/users/month_target/${params.month_target_id}/explain/${params.component_class}/${params.at_date}/`).get().json();
                                    return handle_success(explain);
                                } catch (error) {
                                    return handle_error(error);
                                }
                            }
                        }
                    ]
                }
            ]
        },
        {
            path: "3-mois-glissants",
            async lazy() {
                let { RollingTargetPage } = await import("@/pages/target/rolling-target");
                return { Component: RollingTargetPage };
            },
            children: [
                {
                    path: "api",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const user = url.searchParams.get("user");
                        const month = url.searchParams.get("month");

                        if (!user || !month) {
                            return [];
                        }

                        // month is YYYY-MM-01 (reference month M, typically current).
                        // Window = the 3 completed months before M: [M-3, M-1] (M excluded).
                        const refDate = new Date(month);
                        const startDate = new Date(refDate.getFullYear(), refDate.getMonth() - 3, 1);
                        const endDate = new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1);
                        const monthGte = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-01`;
                        const monthLte = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-01`;

                        const month_targets = (await DataService.client
                            .url("/users/month_target/")
                            .query({
                                user: user,
                                month__gte: monthGte,
                                month__lte: monthLte,
                                page_size: 50
                            })
                            .get()
                            .json()) as PaginatedResponse<MonthTarget>;

                        return month_targets.results;
                    }
                },
                {
                    path: "team",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const month = url.searchParams.get("month");

                        if (!month) {
                            return [];
                        }

                        // Same window as individual view: [M-3, M-1]
                        const refDate = new Date(month);
                        const startDate = new Date(refDate.getFullYear(), refDate.getMonth() - 3, 1);
                        const endDate = new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1);
                        const monthGte = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-01`;
                        const monthLte = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-01`;

                        // Follow pagination: a single page_size=1000 truncates large teams
                        // (3 months × N users can exceed max_page_size).
                        const results: MonthTarget[] = [];
                        let nextUrl: string | null = "/users/month_target/";
                        let isFirstPage = true;

                        while (nextUrl) {
                            const pageRequest = isFirstPage
                                ? DataService.client.url(nextUrl).query({
                                      month__gte: monthGte,
                                      month__lte: monthLte,
                                      page_size: 1000
                                  })
                                : DataService.client.url(nextUrl);
                            const page = (await pageRequest.get().json()) as PaginatedResponse<MonthTarget>;
                            results.push(...(page.results ?? []));
                            nextUrl = page.next ?? null;
                            isFirstPage = false;
                        }

                        return results;
                    }
                }
            ]
        },
        {
            path: "suivi-hebdomadaire",
            async lazy() {
                let { TargetPage } = await import("@/pages/target/week-target");
                return { Component: TargetPage };
            },
            loader: async ({ request }) => {
                const currentDate = new Date();
                const currentStartOfWeek = new Date(currentDate);
                currentStartOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);

                const url = new URL(request.url);
                if (!url.searchParams.has("week")) {
                    url.searchParams.set("week", currentStartOfWeek.toISOString().split("T")[0]);
                }
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                if (!url.searchParams.has("page")) {
                    url.searchParams.set("page", "1");
                }

                const week_target = (await DataService.client
                    .url(`/users/week_target/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json()) as PaginatedResponse<WeekTarget>;

                return { week_target };
            },
            action: async ({ request }) => {
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/users/week_target/${action}/`).post(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            },
            children: [
                {
                    path: ":week_target_id",
                    loader: async ({ params }) => {
                        try {
                            const week_target = await DataService.client.url(`/users/week_target/${params.week_target_id}/details/`).get().json();
                            return handle_success(week_target);
                        } catch (error) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        },
        {
            path: "managed_users",
            loader: async () => {
                const users = await DataService.client.url("/users/month_target/managed_users/").get().json();
                return users;
            }
        },
        {
            path: "sales_processes",
            loader: async () => {
                const processes = await DataService.client.url("/users/month_target/sales_processes/").get().json();
                return processes;
            }
        }
    ]
};

export default TargetRoute;
